import { useEffect, useRef, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useAppSelector } from "../../store/hooks";
import { useGetWelcomeOverviewMessagesQuery } from "./services/GetWelcomeOverviewMessagesApiSlice";
import WelcomeOverviewCarousel from "./WelcomeOverviewCarousel";
import WelcomeOverviewFinalStep from "./WelcomeOverviewFinalStep";
import type { Swiper as SwiperType } from "swiper";
import appLogo from "../../../src/assets/images/app-logo/app_logo.png";
import whiteAppLogo from "../../../src/assets/images/app-logo/app_logo_white.png";
import BackgroundLayout from "../../components/custom-backgrounds/BackgroundLayout";
import { BackgroundVariant } from "../../../src/utility/interfaces/carePlanInterfaces";
interface WelcomeOverviewProps {
  onClose: () => void;
}

const WelcomeOverview: React.FC<WelcomeOverviewProps> = ({ onClose }) => {
  const { patientProfileId, patientCaseId } = useAppSelector(
    (state) => state.patientOnboardingStatus
  );
  const preferredLanguage = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );

  const { data, isLoading, error } = useGetWelcomeOverviewMessagesQuery({
    patientProfileId,
    patientCaseId,
    preferredLanguage,
  });

  const isMobile = useMediaQuery("(max-width: 900px)");
  const welcomeMessages = data?.apiMessages || [];
  const finalStep = welcomeMessages[welcomeMessages.length - 1];
  const carouselMessages = welcomeMessages.slice(0, -1);

  const swiperRef = useRef<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const [showFinalStep, setShowFinalStep] = useState(false);
  const [returningFromFinalStep, setReturningFromFinalStep] = useState(false);
  const [background, setBackground] = useState<BackgroundVariant>(
    isMobile ? "welcomeOverviewMobile" : "welcomeOverviewDesktop"
  );

  const handleNext = () => {
    if (activeIndex === carouselMessages.length - 1) {
      setShowFinalStep(true);
      setBackground(
        isMobile ? "welcomeCoverGreenMobile" : "welcomeCoverGreenDesktop"
      );
    } else {
      swiperRef.current?.slideNext();
    }
  };

  const handleBack = () => {
    setBackground(
      isMobile ? "welcomeOverviewMobile" : "welcomeOverviewDesktop"
    );
    setShowFinalStep(false);
    if (showFinalStep) {
      setReturningFromFinalStep(true);
    } else if (activeIndex > 0) {
      swiperRef.current?.slidePrev();
    }
  };

  const height = {
    xs: "23.88rem",
    sm: "23.88rem",
    md: "20.31rem",
  };

  const finalStepBackgroundHeight = {
    xs: "15.87rem",
    sm: "15.87rem",
    md: "11.31rem",
    lg: "18rem"
  };
  const showBackArrow = showFinalStep || activeIndex > 0;

  useEffect(() => {
    let newBackground = isMobile
      ? "welcomeOverviewMobile"
      : "welcomeOverviewDesktop";

    if (showFinalStep) {
      newBackground = isMobile
        ? "welcomeCoverGreenMobile"
        : "welcomeCoverGreenDesktop";
    } else {
      newBackground = isMobile
        ? "welcomeOverviewMobile"
        : "welcomeOverviewDesktop";
    }
    setBackground(newBackground as BackgroundVariant);
  }, [isMobile, showFinalStep]);

  useEffect(() => {
    if (!showFinalStep && returningFromFinalStep) {
      setReturningFromFinalStep(false);
    }
  }, [showFinalStep, returningFromFinalStep]);

  return (
    <BackgroundLayout
      variant={background}
      height={!showFinalStep ? height : finalStepBackgroundHeight}
    >
      <Box position="relative">
        <img
          src={!showFinalStep ? appLogo : whiteAppLogo}
          alt="Cloud"
          width="200px"
          height="130px"
        />
        {!showFinalStep ? (
          <WelcomeOverviewCarousel
            welcomeOverviewData={carouselMessages}
            isLoading={isLoading}
            error={!!error}
            swiperRef={swiperRef}
            onSlideChange={(index) => setActiveIndex(index)}
            onNextClick={handleNext}
            forceSlideToIndex={
              returningFromFinalStep ? carouselMessages.length - 1 : null
            }
            showBackArrow={showBackArrow}
            handleBack={handleBack}
          />
        ) : (
          <WelcomeOverviewFinalStep
            finalMessage={finalStep}
            onClose={onClose}
            showBackArrow={showBackArrow}
            handleBack={handleBack}
          />
        )}
      </Box>
    </BackgroundLayout>
  );
};

export default WelcomeOverview;
